import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { getApiErrorMessage } from '@/lib/utils/error.utils';
import { cancelSubscription, createPortalSession, updateAutoRenew } from '../api/billing.api';
import { BILLING_STRINGS } from '../constants/billing-strings';
import { billingKeys } from './billing.keys';

const COPY = BILLING_STRINGS.TOAST;

/**
 * The billing mutations a Startup Admin can perform, plus the portal hand-off
 * (FR-41, MYN-113). Data-room seats are not bought since v1.2 (FR-138), so
 * the seat-count mutation that sat here is gone.
 *
 * All of them invalidate `billingKeys.all` rather than a specific key: a
 * cancellation alters status, dates and whether the auto-renewal control is
 * offered at all. Naming individual keys here would mean every future billing
 * surface had to be added to this list to stay correct.
 *
 * ## Where each outcome is reported
 *
 * Success always closes the surface, so success is always a toast. Failure is
 * NOT: `autoRenew` and `cancel` are both confirmed inside a dialog that
 * stays open when the call fails, so their errors render inline beside the
 * control from `mutation.error` - a toast behind an open modal is covered or
 * auto-dismissed unseen. `portal` has no open surface of its own, so it toasts.
 *
 * The inline copy uses the backend's own message, so a refusal reads the same
 * here as the API wrote it.
 */
export function useBillingActions(entityId: string | undefined) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: billingKeys.all });

  const autoRenew = useMutation({
    mutationFn: (value: boolean) => updateAutoRenew(entityId as string, value),
    onSuccess: (_result, value) => {
      toast.success(value ? COPY.AUTO_RENEW_ON : COPY.AUTO_RENEW_OFF);
      void invalidate();
    },
  });

  const cancel = useMutation({
    mutationFn: () => cancelSubscription(entityId as string),
    onSuccess: () => {
      toast.success(COPY.CANCELLED);
      void invalidate();
    },
  });

  /**
   * Opens Stripe's portal in this tab.
   *
   * `window.location.assign` rather than a new tab: the portal's own "return to
   * My NEXT Raise" button brings the member back, and a new tab would leave
   * them with two copies of the app disagreeing about the card on file.
   */
  const portal = useMutation({
    mutationFn: () => createPortalSession(entityId as string),
    onSuccess: ({ portalUrl }) => window.location.assign(portalUrl),
    onError: (error: unknown) => toast.error(getApiErrorMessage(error, COPY.PORTAL_FAILED)),
  });

  return { autoRenew, cancel, portal };
}
